import { defineStore } from "pinia";
import moment, { Moment } from 'moment';

import { StationGroup } from "~/api/models/station-group.model";
import { useStationStore } from "./station.store";
import { DashboardStoreError, DashboardStoreSocketError, DispatchStoreError } from "~/exceptions/store.error";
import io,  { Manager } from 'socket.io-client';
import { useAuthStore } from "./auth.store";
import { useConfigStore } from "./configuration.store";
import { Measure } from "~/api/models/measure.model";
import { Station } from "~/api/models/station.model";
import { DispatchDashboardFilterEnum } from "~/models/dispatch/dispatch-dasboard-filter.enum";
import { StationEvent } from "~/api/models/station-event.model";
import { isStationInGasAlarm, isStationInTrouble, isStationOffline, isStationOk } from "~/misc/station-utils";

type StationMeasures = { [id: string]: Measure[]};
type StationEvents = { [id: string]: StationEvent[]};

export const useDispatchStore = defineStore('dispatch', {

    state: () => {
        const since: Moment = moment().subtract(1, 'month').startOf('day');
        const before: Moment = moment().endOf('days');

        return {
            stations: [] as Station[],
            measures: {} as StationMeasures,
            events: {} as StationEvents,
            ready: false,
    
            since,
            before,

            step: before.diff(since, 'seconds') / 60,
    
            filter: DispatchDashboardFilterEnum.ALL as DispatchDashboardFilterEnum,

            socket: null as Manager,
        }
    },


        actions: {

            initSocket(): void {

                if(this.socket) {
                    this.socket.close();
                    this.socket = null;
                }
    
                try {
                    const authStore = useAuthStore();
                    const group: StationGroup = useStationStore().group;
        
                    const socketEndpoint: string = useConfigStore().socketEndpoint;
                    
                    this.socket = io(socketEndpoint, {
                        query: {
                            topic: `stations.${group?.name}`,
                            token: authStore.accessToken,
                        },
                        withCredentials: true,
                    });
        
                    this.socket.on("measures", () => this.refresh(false));
                } catch(error) {
                    throw new DashboardStoreSocketError('Socket could not be initialized', error);
                }
    
    
            },

            setFilter(filter: DispatchDashboardFilterEnum): void {
                this.filter = filter;
            },

            async refresh(): Promise<void> {

                const group: StationGroup = useStationStore().group;
                if(!group) {
                    throw new DashboardStoreError('No group selected');
                }
    
                const stations: Station[] = useStationStore().stations || [];
                this.stations = stations.filter(s => {
                    switch(this.filter) {
                        case DispatchDashboardFilterEnum.OK:
                            return isStationOk(s);
                        case DispatchDashboardFilterEnum.TROUBLE:
                            return isStationInTrouble(s);
                        case DispatchDashboardFilterEnum.GAS:
                            return isStationInGasAlarm(s);
                        case DispatchDashboardFilterEnum.OFFLINE:
                            return isStationOffline(s);
                        case DispatchDashboardFilterEnum.ALL:
                            return true;
                    }
                })
    
                if (this.ready) {
                    return;
                }
    
                this.ready = true;
                try {

                    this.measures = await Promise.all(
                        stations.map(s => this.$stationService.getMeasures(s.id, this.since))
                    ).then((measures: Measure[][]) => {
                        return measures.reduce((prev: StationMeasures, measures: Measure[], index: number) => {
                            const stationId: string = stations[index].id;
    
                            if(!stationId) {
                                return prev;
                            }
    
                            prev[stationId] = measures;
                            return prev;
    
                        }, {});
                    });

                    this.events = await Promise.all(
                        stations.map(s => this.$stationService.getStationEvents(s.id, this.since))
                    ).then((events: StationEvent[][]) => {
                        return events.reduce((prev: StationEvents, events: StationEvent[], index: number) => {
                            const stationId: string = stations[index].id;
    
                            if(!stationId) {
                                return prev;
                            }
    
                            prev[stationId] = events;
                            return prev;
    
                        }, {});
                    });

                    this.ready = false;
                } catch(error) {
                    this.ready = false;
                    throw new DispatchStoreError('Could not refresh dispatch dashboard', error)
                }
    
                this.ready = false;
            },
        }
});